(() => {
  'use strict';

  const roots = document.querySelectorAll('[data-f2w-sport-detail]');
  if (!roots.length) return;

  const NODES_QUERY = `
    query F2WSportChapter($ids: [ID!]!, $language: LanguageCode!) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          handle
          onlineStoreUrl
          name: field(key: "name") { value }
          displayName: field(key: "display_name") { value }
          nickname: field(key: "nickname") { value }
          abbreviation: field(key: "abbreviation") { value }
          resolvedImageUrl: field(key: "resolved_image_url") { value }
          city: field(key: "city") { value }
          cityReference: field(key: "city_reference") {
            reference { ... on Metaobject { name: field(key: "name") { value } } }
          }
          homeGym: field(key: "home_gym") {
            reference { ... on Metaobject { id name: field(key: "name") { value } } }
          }
          birthCity: field(key: "birth_city") {
            reference { ... on Metaobject { name: field(key: "name") { value } } }
          }
          locationType: field(key: "location_type") { value }
          editorialProfile: field(key: "editorial_profile") { value }
          dateStart: field(key: "date_start") { value }
          dateEnd: field(key: "date_end") { value }
          isHistoric: field(key: "is_historic") { value }
          isLegend: field(key: "is_legend") { value }
          locationName: field(key: "location_name") { value }
          country: field(key: "country") {
            reference {
              ... on Metaobject {
                id
                name: field(key: "name") { value }
                code: field(key: "code") { value }
              }
            }
          }
          nationality: field(key: "nationality") {
            reference {
              ... on Metaobject {
                id
                name: field(key: "name") { value }
                code: field(key: "code") { value }
              }
            }
          }
          venue: field(key: "venue") {
            reference {
              ... on Metaobject { name: field(key: "name") { value } }
            }
          }
          organization: field(key: "organization") {
            reference {
              ... on Metaobject { name: field(key: "name") { value } }
            }
          }
          sports: field(key: "sports") {
            references(first: 50) {
              nodes { ... on Metaobject { name: field(key: "name") { value } } }
            }
          }
        }
      }
    }
  `;

  const COUNTRY_NAMES_QUERY = `
    query F2WSportCountries($ids: [ID!]!, $language: LanguageCode!) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject { id name: field(key: "name") { value } code: field(key: "code") { value } }
      }
    }
  `;

  const normalize = (value) => String(value || '').trim();
  const fold = (value) => normalize(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const unique = (values) => [...new Set(values.map(normalize).filter(Boolean))];

  const chunk = (values, size) => {
    const chunks = [];
    for (let index = 0; index < values.length; index += size) {
      chunks.push(values.slice(index, index + size));
    }
    return chunks;
  };

  const nativeUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(String(value), window.location.origin);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
      const root = window.Shopify?.routes?.root || '/';
      const prefix = root === '/' ? '' : root.replace(/\/$/, '');
      let path = url.pathname;
      if (/^\/[a-z]{2}(\/|$)/i.test(path)) path = path.replace(/^\/[a-z]{2}/i, '');
      if (prefix && path.startsWith(`${prefix}/`)) path = path.slice(prefix.length);
      return `${prefix}${path}${url.search}${url.hash}`;
    } catch (error) {
      return '';
    }
  };

  const make = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = text;
    return element;
  };

  const countryFlag = (code) => {
    const flagCode = normalize(code).toLowerCase();
    if (!/^[a-z]{2}$/.test(flagCode) && !['gb-eng', 'gb-sct', 'gb-wls'].includes(flagCode)) return null;
    const wrapper = make('span', 'f2w-country-flag');
    wrapper.setAttribute('aria-hidden', 'true');
    const image = document.createElement('img');
    image.src = `https://flagcdn.com/w40/${flagCode}.png`;
    image.srcset = `https://flagcdn.com/w80/${flagCode}.png 2x`;
    image.alt = '';
    image.width = 20;
    image.height = 15;
    image.style.width = '20px';
    image.style.height = '15px';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.addEventListener('error', () => {
      const iso = flagCode.startsWith('gb-') ? 'GB' : flagCode.toUpperCase();
      wrapper.textContent = `${String.fromCodePoint(...[...iso].map((letter) => letter.charCodeAt(0) + 127397))}\u00a0`;
    }, { once: true });
    wrapper.append(image, document.createTextNode('\u00a0'));
    return wrapper;
  };

  const request = async (endpoint, token, query, variables) => {
    if (!token) throw new Error('Missing Storefront token');
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query, variables })
    });
    if (!response.ok) throw new Error(`Storefront request failed: ${response.status}`);
    const payload = await response.json();
    if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join('; '));
    return payload.data || {};
  };

  const parseBoolean = (value) => ['true', '1', 'yes'].includes(fold(value));
  const formatDate = (value, language) => {
    const dateValue = normalize(value).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return '';
    const date = new Date(`${dateValue}T12:00:00Z`);
    return new Intl.DateTimeFormat(language.toLowerCase(), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
  };
  const dateBlock = (value, language) => {
    const dateValue = normalize(value).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return null;
    const date = new Date(`${dateValue}T12:00:00Z`);
    const parts = new Intl.DateTimeFormat(language.toLowerCase(), { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).formatToParts(date);
    const part = (type) => parts.find((item) => item.type === type)?.value || '';
    const block = make('div', 'f2w-event-card__date-block');
    block.append(make('span', 'f2w-event-card__date-day', part('day')));
    block.append(make('span', 'f2w-event-card__date-month', part('month')));
    block.append(make('span', 'f2w-event-card__date-year', part('year')));
    return block;
  };

  const mapNode = (node, chapter) => {
    if (!node) return null;
    const name = normalize(node.name?.value) || normalize(node.displayName?.value) || normalize(node.handle);
    if (!name) return null;
    const country = node.country?.reference || node.nationality?.reference || null;
    const countryName = normalize(country?.name?.value);
    const city = chapter === 'events' || chapter === 'historic'
      ? normalize(node.cityReference?.reference?.name?.value)
      : normalize(node.city?.value) || (chapter === 'fighters' || chapter === 'legends' ? '' : normalize(node.birthCity?.reference?.name?.value));
    const homeGym = chapter === 'fighters' ? normalize(node.homeGym?.reference?.name?.value) : '';
    const venueName = normalize(node.venue?.reference?.name?.value);
    const locationName = normalize(node.locationName?.value);
    const organizationName = normalize(node.organization?.reference?.name?.value);
    const sports = (node.sports?.references?.nodes || []).map((sport) => normalize(sport.name?.value)).filter(Boolean);
    const dateStart = normalize(node.dateStart?.value).slice(0, 10);
    const dateEnd = normalize(node.dateEnd?.value).slice(0, 10);
    const isHistoric = parseBoolean(node.isHistoric?.value);
    const location = [locationName || venueName, city].filter(Boolean).join(', ');
    return {
      id: normalize(node.id),
      name,
      url: nativeUrl(node.onlineStoreUrl),
      image: normalize(node.resolvedImageUrl?.value),
      country: countryName,
      countryCode: normalize(country?.code?.value),
      city,
      homeGym,
      location,
      venue: venueName,
      organization: organizationName,
      sports,
      nickname: normalize(node.nickname?.value),
      abbreviation: normalize(node.abbreviation?.value),
      locationType: normalize(node.locationType?.value).toLowerCase(),
      editorialProfile: normalize(node.editorialProfile?.value).toLowerCase(),
      dateStart,
      dateEnd,
      date: dateStart,
      isHistoric,
      isLegend: parseBoolean(node.isLegend?.value),
      search: fold([name, nicknameValue(node), countryName, city, location, organizationName, ...sports].join(' '))
    };
  };

  const nicknameValue = (node) => normalize(node.nickname?.value);

  const renderCountry = (item) => {
    if (!item.country) return null;
    const line = make('p', 'f2w-event-card__location f2w-country-line');
    const flag = countryFlag(item.countryCode, item.country);
    if (flag) line.appendChild(flag);
    line.appendChild(document.createTextNode(item.country));
    return line;
  };

  const renderTopline = (item, chapter, label) => {
    const topline = make('div', 'f2w-event-card__topline');
    const add = (text, red) => {
      if (!text) return;
      topline.appendChild(make('span', red ? 'f2w-event-card__period f2w-event-card__period--upcoming' : 'f2w-event-card__period', text));
    };

    if (chapter === 'fighters' && item.nickname) add(item.nickname, true);
    if (chapter === 'legends') {
      add(label || 'Legendes', true);
      if (item.nickname) add(item.nickname, false);
    }
    if ((chapter === 'venues' || chapter === 'iconic') && item.city) add(item.city, true);
    if (chapter === 'organizations' && item.abbreviation) add(item.abbreviation, true);
    if (chapter === 'events' && item.organization) add(item.organization, true);
    return topline.childNodes.length ? topline : null;
  };

  const renderCard = (item, chapter, labels, chapterLabel) => {
    const article = make('article', 'f2w-event-card');
    const link = make(item.url ? 'a' : 'div', 'f2w-event-card__link');
    if (item.url) link.href = item.url;

    const imageWrap = make('div', 'f2w-event-card__image');
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.name;
      image.width = 1100;
      image.height = 825;
      image.loading = 'lazy';
      image.decoding = 'async';
      imageWrap.appendChild(image);
    } else {
      imageWrap.appendChild(make('div', 'f2w-event-card__image--placeholder', item.name.slice(0, 1)));
    }

    const body = make('div', 'f2w-event-card__body');
    if (chapter === 'events' || chapter === 'historic') {
      const block = dateBlock(item.dateStart, labels.language);
      if (block) body.appendChild(block);
    }

    const content = make('div', 'f2w-event-card__content');
    const topline = renderTopline(item, chapter, chapterLabel);
    if (topline) content.appendChild(topline);
    content.appendChild(make('h2', 'f2w-event-card__title', item.name));

    if ((chapter === 'events' || chapter === 'historic') && item.dateEnd && item.dateEnd !== item.dateStart) {
      const endDate = formatDate(item.dateEnd, labels.language);
      content.appendChild(make('p', 'f2w-event-card__date-range', `${labels.until ? `${labels.until} ` : ''}${endDate}`));
    }
    if ((chapter === 'events' || chapter === 'historic') && item.location) {
      content.appendChild(make('p', 'f2w-event-card__location', item.location));
    }
    const countryLine = renderCountry(item);
    if (countryLine) content.appendChild(countryLine);
    if (item.sports.length && (chapter === 'events' || chapter === 'historic')) {
      const sports = make('div', 'f2w-event-card__sports');
      item.sports.forEach((sport) => sports.appendChild(make('span', 'f2w-event-card__sport', sport)));
      content.appendChild(sports);
    }
    if (item.url) content.appendChild(make('span', 'f2w-event-card__view', labels.view));
    body.appendChild(content);
    link.append(imageWrap, body);
    article.appendChild(link);
    return article;
  };

  const init = (root) => {
    if (root.dataset.f2wInitialized === 'true') return;
    root.dataset.f2wInitialized = 'true';

    const token = root.dataset.storefrontToken || '';
    const shop = root.dataset.shopDomain || window.Shopify?.shop || window.location.hostname;
    const endpoint = `https://${shop}/api/${root.dataset.apiVersion || '2024-10'}/graphql.json`;
    const language = (root.dataset.language || document.documentElement?.lang || 'nl').split('-')[0].toUpperCase();
    const perPage = Math.max(1, parseInt(root.dataset.perPage, 10) || 24);
    const largePerPage = 24;
    const numberFormatter = new Intl.NumberFormat(root.dataset.language || 'nl');
    const formatCount = (value) => numberFormatter.format(value);
    root.querySelectorAll('.f2w-fightbase-hub__hero-number, [data-f2w-sport-hero-counters] [data-f2w-sport-jump], [data-f2w-sport-hero-counters] .f2w-city-detail__parent > span:last-child').forEach((node) => {
      const raw = node.textContent.trim();
      if (/^\d+$/.test(raw)) node.textContent = formatCount(Number(raw));
    });
    const labels = {
      language,
      loading: root.dataset.labelLoading || 'Laden...',
      empty: root.dataset.labelEmpty || 'Geen resultaten gevonden.',
      error: root.dataset.labelError || 'De gegevens konden niet worden geladen.',
      view: root.dataset.labelView || 'Bekijk profiel',
      results: root.dataset.labelResults || 'resultaten',
      countryPrompt: root.dataset.labelCountryPrompt || '',
      unknownCountry: root.dataset.labelUnknownCountry || '',
      countryEmpty: root.dataset.labelCountryEmpty || '',
      paginationPage: root.dataset.labelPaginationPage || '',
      paginationPrevious: root.dataset.labelPaginationPrevious || '',
      paginationNext: root.dataset.labelPaginationNext || '',
      until: root.dataset.labelUntil || ''
    };

    let index = {};
    try {
      index = JSON.parse(root.querySelector('[data-f2w-sport-index]')?.textContent || '{}') || {};
    } catch (error) {
      index = {};
    }

    const panels = new Map([...root.querySelectorAll('[data-f2w-sport-chapter]')].map((panel) => [panel.dataset.f2wSportChapter, panel]));
    const tabs = new Map([...root.querySelectorAll('[data-f2w-sport-tab]')].map((tab) => [tab.dataset.f2wSportTab, tab]));
    const states = new Map();
    const chapterLabel = (chapter) => normalize(tabs.get(chapter)?.textContent) || chapter;

    const hydrate = async (chapter, ids) => {
      const nodes = [];
      for (const batch of chunk(ids, 250)) {
        const data = await request(endpoint, token, NODES_QUERY, { ids: batch, language });
        if (!Array.isArray(data.nodes)) throw new Error('Chapter nodes unavailable');
        const received = new Set(data.nodes.filter(Boolean).map((node) => node.id));
        if (batch.some((id) => !received.has(id))) throw new Error('Chapter hydration incomplete');
        nodes.push(...data.nodes.filter(Boolean));
      }
      let items = nodes.map((node) => mapNode(node, chapter)).filter(Boolean);
      if (items.length !== ids.length) throw new Error('Chapter profile data incomplete');
      if (chapter === 'events') {
        const todayParts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
        const today = `${todayParts.find((part) => part.type === 'year')?.value}-${todayParts.find((part) => part.type === 'month')?.value}-${todayParts.find((part) => part.type === 'day')?.value}`;
        items = items.filter((item) => !item.isHistoric && (item.dateEnd || item.date) >= today).sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name));
      } else if (chapter === 'historic') {
        items = items.filter((item) => item.isHistoric).sort((a, b) => b.date.localeCompare(a.date) || a.name.localeCompare(b.name));
      } else {
        const curated = index.curated?.[chapter] || [];
        const rank = new Map(curated.map((id, position) => [id, position]));
        items.sort((a, b) => (rank.has(a.id) ? rank.get(a.id) : Infinity) - (rank.has(b.id) ? rank.get(b.id) : Infinity) || a.name.localeCompare(b.name, language.toLowerCase()));
      }
      return items;
    };

    const fillSelect = (select, values, keep) => {
      if (!select) return;
      const first = select.options[0];
      select.replaceChildren(first);
      sorted(values).forEach((value) => select.appendChild(new Option(value, value)));
      select.value = values.includes(keep) ? keep : '';
    };
    const sorted = (values) => unique(values).sort((a, b) => a.localeCompare(b, language.toLowerCase()));

    const applyFilters = (chapter) => {
      const state = states.get(chapter);
      const panel = panels.get(chapter);
      if (!state || !panel) return;
      const countrySelect = panel.querySelector('[data-f2w-sport-filter-country]');
      const citySelect = panel.querySelector('[data-f2w-sport-filter-city]');
      const cityWrap = panel.querySelector('[data-f2w-sport-filter-wrap="city"]');
      const searchInput = panel.querySelector('[data-f2w-sport-filter-search]');
      const chosen = state.gated ? (countrySelect?.value || '') : (state.selectedSmallCountry ?? countrySelect?.value ?? '');
      const country = state.gated ? (chosen === '__missing_country__' ? '' : state.countryNames.get(chosen) || '') : chosen;
      const inCountry = state.items.filter((item) => !country || item.country === country);
      let city = '';
      if (state.gated) {
        const previous = citySelect?.value || '';
        const grouped = new Map();
        for (const item of inCountry) {
          const name = normalize(chapter === 'fighters' ? item.homeGym : item.city);
          const key = fold(name);
          if (!key) continue;
          if (!grouped.has(key)) grouped.set(key, { key, count: 0, variants: new Map() });
          const group = grouped.get(key);
          group.count++;
          group.variants.set(name, (group.variants.get(name) || 0) + 1);
        }
        state.cityChoices = [...grouped.values()].map((group) => ({
          key: group.key,
          count: group.count,
          name: [...group.variants].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], language.toLowerCase()))[0][0]
        }));
        citySelect.replaceChildren(citySelect.options[0], ...state.cityChoices.map((choice) => new Option(choice.name, choice.key)));
        citySelect.value = state.cityChoices.some((choice) => choice.key === previous) ? previous : '';
        const showCities = !!country && state.cityChoices.length > 1 && (chapter !== 'fighters' || inCountry.length > 48);
        if (cityWrap) cityWrap.hidden = true;
        const cityPicker = panel.querySelector('[data-f2w-sport-city-picker]');
        if (cityPicker) cityPicker.hidden = !showCities;
        const filters = panel.querySelector('[data-f2w-sport-filters]');
        if (filters) filters.dataset.f2wSportCityChips = String(showCities);
        if (showCities) state.renderCityPicker?.();
        city = showCities ? citySelect.value : '';
      } else {
        const secondary = chapter === 'fighters' ? 'homeGym' :
          ['legends', 'trainers', 'masters', 'founders', 'promoters'].includes(chapter) ? '' : 'city';
        const candidates = secondary ? inCountry.map((item) => item[secondary]) : [];
        const cities = sorted(candidates);
        fillSelect(citySelect, cities, citySelect?.value || '');
        const showSecondary = !!country && cities.length > 1 &&
          (chapter !== 'fighters' || inCountry.length > 48);
        if (cityWrap) cityWrap.hidden = true;
        const cityPicker = panel.querySelector('[data-f2w-sport-city-picker]');
        if (cityPicker) cityPicker.hidden = !showSecondary;
        city = showSecondary ? citySelect.value : '';
        state.renderSmallSecondary?.(cities, showSecondary);
        state.renderSmallCountry?.();
      }
      const query = fold(searchInput?.value || '');
      const matchesSecondary = (item) => !city || (state.gated ? fold(chapter === 'fighters' ? item.homeGym : item.city) === city : (chapter === 'fighters' ? item.homeGym : item.city) === city);
      const preSearch = inCountry.filter(matchesSecondary);
      const searchWrap = searchInput?.closest('.f2w-event-directory__filter');
      if (searchWrap) searchWrap.hidden = preSearch.length <= 48 && !query;
      const filters = panel.querySelector('[data-f2w-sport-filters]');
      if (filters) filters.hidden = !!searchWrap?.hidden;
      state.filtered = preSearch.filter((item) => !query || item.search.includes(query));
      state.visible = Math.min(perPage, state.filtered.length);
      state.page = 1;
      render(chapter);
    };

    const renderPagination = (chapter, state, grid, more) => {
      if (!state.pagination) {
        const navigation = make('nav', 'f2w-event-directory__tabs');
        navigation.dataset.f2wSportPagination = '';
        navigation.setAttribute('aria-label', labels.paginationPage);
        more?.parentElement?.appendChild(navigation);
        state.pagination = navigation;
      }
      const navigation = state.pagination;
      const total = Math.ceil(state.filtered.length / largePerPage);
      navigation.hidden = total <= 1;
      if (navigation.hidden) { navigation.replaceChildren(); return; }
      const page = Math.min(Math.max(1, state.page), total);
      state.page = page;
      const go = (target) => {
        state.page = Math.min(Math.max(1, target), total);
        render(chapter);
        state.pagination.querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      const button = (text, target, active, disabled) => {
        const element = make('button', 'f2w-event-directory__tab', text);
        element.type = 'button';
        element.disabled = disabled;
        if (/^\d+$/.test(text)) element.setAttribute('aria-label', `${labels.paginationPage} ${target}`);
        if (active) element.setAttribute('aria-current', 'page');
        if (!active) element.addEventListener('click', () => go(target));
        return element;
      };
      const arrow = (symbol, label, target, disabled) => {
        const element = button(symbol, target, false, disabled);
        element.classList.add('f2w-sport-detail__page-arrow');
        element.setAttribute('aria-label', label);
        element.title = label;
        return element;
      };
      const controls = [];
      if (page > 1) controls.push(arrow('◀', labels.paginationPrevious, page - 1, false));
      const pages = [...new Set([1, page - 1, page, page + 1, total].filter((n) => n >= 1 && n <= total))].sort((a, b) => a - b);
      let previous = 0;
      for (const number of pages) {
        if (number - previous > 1) controls.push(make('span', '', '…'));
        controls.push(button(String(number), number, number === page, false));
        previous = number;
      }
      if (page < total) controls.push(arrow('▶', labels.paginationNext, page + 1, false));
      navigation.replaceChildren(...controls);
    };

    const render = (chapter) => {
      const state = states.get(chapter);
      const panel = panels.get(chapter);
      const grid = panel.querySelector('[data-f2w-sport-results]');
      const status = panel.querySelector('[data-f2w-sport-status]');
      const more = panel.querySelector('[data-f2w-sport-more]');
      const size = largePerPage;
      state.page = Math.min(Math.max(1, state.page), Math.max(1, Math.ceil(state.filtered.length / size)));
      const start = (state.page - 1) * size;
      const end = start + size;
      grid.replaceChildren(...state.filtered.slice(start, end).map((item) => renderCard(item, chapter, labels, chapterLabel(chapter))));
      if (status) status.textContent = state.filtered.length ? `${formatCount(state.filtered.length)} ${labels.results}` : labels.empty;
      if (more) more.hidden = true;
      renderPagination(chapter, state, grid, more);
    };

    const setupFilters = (chapter) => {
      const state = states.get(chapter);
      const panel = panels.get(chapter);
      const filters = panel.querySelector('[data-f2w-sport-filters]');
      const countrySelect = panel.querySelector('[data-f2w-sport-filter-country]');
      const citySelect = panel.querySelector('[data-f2w-sport-filter-city]');
      const searchInput = panel.querySelector('[data-f2w-sport-filter-search]');
      const more = panel.querySelector('[data-f2w-sport-more]');
      if (!state.gated) {
        fillSelect(countrySelect, state.items.map((item) => item.country), '');
        countrySelect?.addEventListener('change', () => { state.selectedSmallCountry = countrySelect.value; state.countryAllChosen = countrySelect.value === ''; if (citySelect) citySelect.value = ''; state.showAllCities = false; state.secondaryAllChosen = false; if (searchInput) searchInput.value = ''; applyFilters(chapter); });
        state.setupSmallPickers?.();
        const searchWrap = searchInput?.closest('.f2w-event-directory__filter');
        if (searchWrap) searchWrap.hidden = state.items.length <= 48;
      }
      citySelect?.addEventListener('change', () => { state.secondaryAllChosen = citySelect.value === ''; if (searchInput) searchInput.value = ''; applyFilters(chapter); });
      let timer;
      searchInput?.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => applyFilters(chapter), 200); });

      if (filters) { filters.hidden = false; filters.dataset.f2wSportSearchOnly = 'true'; if (!state.gated) filters.dataset.f2wSportCityChips = 'true'; }
    };

    const loadChapter = async (chapter) => {
      const panel = panels.get(chapter);
      if (!panel || !panel.querySelector('[data-f2w-sport-results]') || states.has(chapter)) return;
      const allIds = unique(index[chapter] || []);
      const regionRows = index.regions?.[chapter] || [];
      const gated = (chapter === 'gyms' || chapter === 'fighters') && allIds.length > 250 && regionRows.length === allIds.length;
      const state = { items: [], filtered: [], visible: 0, page: 1, gated, countryNames: new Map(), generation: 0 };
      states.set(chapter, state);
      const status = panel.querySelector('[data-f2w-sport-status]');
      if (status) status.textContent = labels.loading;

      if (gated) {
        const countrySelect = panel.querySelector('[data-f2w-sport-filter-country]');
        const picker = panel.querySelector('[data-f2w-sport-country-picker]');
        const quick = panel.querySelector('[data-f2w-sport-country-quick]');
        const moreCountries = panel.querySelector('[data-f2w-sport-country-more]');
        const countryPanel = panel.querySelector('[data-f2w-sport-country-panel]');
        const countryList = panel.querySelector('[data-f2w-sport-country-list]');
        const selectWrap = panel.querySelector('[data-f2w-sport-country-select-wrap]');
        const searchInput = panel.querySelector('[data-f2w-sport-filter-search]');
        const filters = panel.querySelector('[data-f2w-sport-filters]');
        const citySelect = panel.querySelector('[data-f2w-sport-filter-city]');
        const cityWrap = panel.querySelector('[data-f2w-sport-filter-wrap="city"]');
        const cityPicker = panel.querySelector('[data-f2w-sport-city-picker]');
        const cityQuick = panel.querySelector('[data-f2w-sport-city-quick]');
        const more = panel.querySelector('[data-f2w-sport-more]');
        const grid = panel.querySelector('[data-f2w-sport-results]');
        const countryForId = new Map(regionRows.map((entry) => {
          const separator = entry.indexOf('~');
          const countryId = entry.slice(separator + 1);
          return [entry.slice(0, separator), countryId.startsWith('gid://shopify/Metaobject/') ? countryId : '__missing_country__'];
        }));
        // The active Netherlands and Belgium Country records are pinned for Fight2Win's main markets.
        const pinnedCountryIds = ['gid://shopify/Metaobject/455120027914', 'gid://shopify/Metaobject/455120126218'];
        const countryIds = unique([...pinnedCountryIds, ...countryForId.values()].filter((id) => id.startsWith('gid://shopify/Metaobject/')));
        const names = new Map();
        const countryCodes = new Map();
        countrySelect.replaceChildren(new Option(labels.countryPrompt, ''), new Option(countrySelect.options[0]?.textContent || '', '__all__'));
        if (searchInput) searchInput.disabled = true;
        if (selectWrap) selectWrap.hidden = true;
        if (filters) filters.hidden = true;
        const cache = new Map();
        const selectCity = (key) => {
          state.showAllCities = key === '' && state.cityChoices.length > 6 ? !state.showAllCities : false;
          state.secondaryAllChosen = key === '';
          citySelect.value = key;
          if (searchInput) searchInput.value = '';
          applyFilters(chapter);
          cityQuick.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
        };
        const cityButton = (key, label) => {
          const button = make('button', 'f2w-event-card__sport', label);
          button.type = 'button';
          button.setAttribute('aria-pressed', String(citySelect.value === key && (key !== '' || !!state.secondaryAllChosen)));
          if (key === '') button.setAttribute('aria-expanded', String(!!state.showAllCities));
          button.addEventListener('click', () => selectCity(key));
          return button;
        };
        state.renderCityPicker = () => {
          const ranked = [...(state.cityChoices || [])].sort((a, b) => b.count - a.count
            || a.name.localeCompare(b.name, language.toLowerCase()));
          const featured = ranked.slice(0, 6);
          const chosen = citySelect.value;
          if (chosen && !featured.some((choice) => choice.key === chosen)) {
            const selected = ranked.find((choice) => choice.key === chosen);
            if (selected) { featured.pop(); featured.unshift(selected); }
          }
          const extras = state.showAllCities ? ranked.filter((choice) => !featured.includes(choice))
            .sort((a, b) => a.name.localeCompare(b.name, language.toLowerCase())) : [];
          const allLabel = citySelect.options[0]?.textContent || '';
          cityQuick.replaceChildren(cityButton('', allLabel),
            ...[...featured, ...extras].map((choice) => cityButton(choice.key, choice.name)));
        };
        state.resetCityPicker = () => {
          citySelect.value = '';
          state.showAllCities = false;
          state.secondaryAllChosen = false;
          cityPicker.hidden = true;
          cityQuick.replaceChildren();
          if (filters) filters.dataset.f2wSportCityChips = 'false';
        };
        const countryCounts = new Map();
        for (const id of allIds) {
          const countryId = countryForId.get(id);
          countryCounts.set(countryId, (countryCounts.get(countryId) || 0) + 1);
        }
        const selectCountry = (id) => {
          countrySelect.value = id;
          countryPanel.hidden = id !== '__all__';
          moreCountries.setAttribute('aria-expanded', String(!countryPanel.hidden)); moreCountries.textContent = countryMoreLabel;
          state.selectCountry();
          (quick.querySelector('[aria-pressed="true"]') || moreCountries).focus({ preventScroll: true });
        };
        const countryButton = (id, label) => {
          const button = make('button', 'f2w-event-card__sport');
          button.type = 'button';
          const flag = countryFlag(countryCodes.get(id));
          if (flag) button.appendChild(flag);
          button.appendChild(document.createTextNode(label));
          button.setAttribute('aria-pressed', String(countrySelect.value === id));
          button.addEventListener('click', () => selectCountry(id));
          return button;
        };
        const renderCountryList = () => {
          const countryIds = [...state.countryNames.keys()].sort((a, b) =>
            state.countryNames.get(a).localeCompare(state.countryNames.get(b), language.toLowerCase()));
          const entries = [];
          for (const id of countryIds) {
            if (state.countryFeaturedIds?.has(id)) continue;
            const name = state.countryNames.get(id);
            entries.push(countryButton(id, name));
          }
          countryList.replaceChildren(...entries);
        };
        state.renderCountryPicker = () => {
          const ranked = [...state.countryNames.keys()].filter((id) => id !== '__missing_country__')
            .sort((a, b) => (countryCounts.get(b) || 0) - (countryCounts.get(a) || 0)
              || state.countryNames.get(a).localeCompare(state.countryNames.get(b), language.toLowerCase()));
          const pinned = pinnedCountryIds.filter((id) => state.countryNames.has(id));
          const featured = [...pinned, ...ranked.filter((id) => !pinned.includes(id)).slice(0, 6 - pinned.length)];
          const chosen = countrySelect.value;
          if (chosen && chosen !== '__all__' && state.countryNames.has(chosen) && !featured.includes(chosen)) {
            if (featured.length >= 6) featured.pop();
            featured.splice(pinned.length, 0, chosen);
          }
          const allLabel = countrySelect.options[1]?.textContent || '';
          const compact = Boolean(chosen && chosen !== '__all__');
          const visible = compact ? (state.countryNames.has(chosen) ? [chosen] : []) : featured;
          const buttons = [countryButton('__all__', allLabel), ...visible.map((id) => countryButton(id, state.countryNames.get(id)))];
          state.countryFeaturedIds = new Set(visible);
          if (compact) countryPanel.hidden = true;
          else if (chosen === '__all__') countryPanel.hidden = false;
          moreCountries.textContent = countryMoreLabel;
          moreCountries.setAttribute('aria-expanded', String(!countryPanel.hidden));
          moreCountries.hidden = compact || !countryPanel.hidden || state.countryNames.size <= visible.length;
          quick.replaceChildren(...buttons, countryPanel, moreCountries);
          renderCountryList();
        };
        const countryMoreLabel = moreCountries.textContent;
        const syncCountryMore = () => { moreCountries.setAttribute('aria-expanded', String(!countryPanel.hidden)); moreCountries.textContent = countryMoreLabel; moreCountries.hidden = !countryPanel.hidden || Boolean(countrySelect.value && countrySelect.value !== '__all__'); };
        moreCountries.addEventListener('click', () => {
          countryPanel.hidden = !countryPanel.hidden;
          syncCountryMore();
          if (!countryPanel.hidden) { renderCountryList(); countryList.querySelector('button')?.focus({ preventScroll: true }); }
        });
        countryPanel.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          countryPanel.hidden = true;
          syncCountryMore();
          if (!moreCountries.hidden) moreCountries.focus();
        });
        state.selectCountry = async () => {
          const generation = ++state.generation;
          const chosen = countrySelect.value;
          state.resetCityPicker();
          if (cityWrap) cityWrap.hidden = true;
          if (searchInput) { searchInput.value = ''; searchInput.disabled = true; }
          state.items = [];
          state.filtered = [];
          state.page = 1;
          if (state.pagination) state.pagination.hidden = true;
          if (grid) grid.replaceChildren();
          if (more) more.hidden = true;
          if (filters) {
            filters.hidden = true;
            filters.dataset.f2wSportAllCountries = String(chosen === '__all__' || chosen === '__missing_country__');
          }
          state.renderCountryPicker();
          if (!chosen) { if (status) status.textContent = labels.countryPrompt; return; }
          if (status) status.textContent = labels.loading;
          const selectedIds = chosen === '__all__' ? allIds : allIds.filter((id) => countryForId.get(id) === chosen);
          try {
            const items = cache.has(chosen) ? cache.get(chosen) : await hydrate(chapter, selectedIds);
            if (generation !== state.generation) return;
            cache.set(chosen, items);
            state.items = items;
            if (!state.filtersReady) { setupFilters(chapter); state.filtersReady = true; }
            if (filters) filters.hidden = false;
            if (searchInput) searchInput.disabled = false;
            applyFilters(chapter);
          } catch (error) {
            if (generation === state.generation && status) status.textContent = labels.error;
          }
        };
        countrySelect.addEventListener('change', state.selectCountry);
        try {
          for (const batch of chunk(countryIds, 250)) {
            const data = await request(endpoint, token, COUNTRY_NAMES_QUERY, { ids: batch, language });
            for (const node of data.nodes || []) {
              const name = normalize(node?.name?.value);
              if (node?.id && name) {
                names.set(node.id, name);
                if (node.code?.value) countryCodes.set(node.id, normalize(node.code.value));
              }
            }
          }
          const sortedIds = [...names.keys()].sort((a, b) => names.get(a).localeCompare(names.get(b), language.toLowerCase()));
          for (const id of sortedIds) {
            const option = new Option(names.get(id), id);
            countrySelect.appendChild(option);
            state.countryNames.set(id, names.get(id));
          }
          if ([...countryForId.values()].includes('__missing_country__')) {
            countrySelect.appendChild(new Option(labels.unknownCountry, '__missing_country__'));
            state.countryNames.set('__missing_country__', labels.unknownCountry);
          }
          picker.hidden = false;
          moreCountries.hidden = false;
          state.renderCountryPicker();
          if (status && !countrySelect.value) status.textContent = labels.countryPrompt;
        } catch (error) {
          picker.hidden = false;
          moreCountries.hidden = false;
          state.renderCountryPicker();
          if (status) status.textContent = labels.error;
        }
        return;
      }

      const setupSmallPickers = () => {
        const countrySelect = panel.querySelector('[data-f2w-sport-filter-country]');
        const citySelect = panel.querySelector('[data-f2w-sport-filter-city]');
        const picker = panel.querySelector('[data-f2w-sport-country-picker]');
        const quick = panel.querySelector('[data-f2w-sport-country-quick]');
        const moreCountries = panel.querySelector('[data-f2w-sport-country-more]');
        const countryPanel = panel.querySelector('[data-f2w-sport-country-panel]');
        const countryList = panel.querySelector('[data-f2w-sport-country-list]');
        const cityPicker = panel.querySelector('[data-f2w-sport-city-picker]');
        const cityQuick = panel.querySelector('[data-f2w-sport-city-quick]');
        const countryWrap = panel.querySelector('[data-f2w-sport-country-select-wrap]');
        const makeChoice = (value, label, select, onChoose) => {
          const button = make('button', 'f2w-event-card__sport', label);
          button.type = 'button';
          button.setAttribute('aria-pressed', String(select.value === value));
          button.addEventListener('click', () => onChoose(value));
          return button;
        };
        const countBy = (get) => {
          const counts = new Map();
          for (const item of state.items) { const key = get(item); if (key) counts.set(key, (counts.get(key) || 0) + 1); }
          return counts;
        };
        const countryCounts = countBy((item) => item.country);
        state.hasCountryChoices = countryCounts.size > 1;
        if (countryCounts.size === 1 && state.items.every((item) => item.country === [...countryCounts.keys()][0])) countrySelect.value = [...countryCounts.keys()][0];
        state.selectedSmallCountry = countrySelect.value;
        const codes = new Map();
        const codeForName = new Map();
        for (const item of state.items) if (item.country && item.countryCode) {
          codes.set(item.countryCode.toUpperCase(), item.country);
          codeForName.set(item.country, item.countryCode);
        }
        const countryMoreLabel = moreCountries.textContent;
        const chooseCountry = (value) => {
          state.selectedSmallCountry = value;
          state.countryAllChosen = value === '';
          countrySelect.value = value;
          citySelect.value = '';
          state.showAllCities = false;
          state.secondaryAllChosen = false;
          const resultsSearch = panel.querySelector('[data-f2w-sport-filter-search]');
          if (resultsSearch) resultsSearch.value = '';
          countryPanel.hidden = true;
          moreCountries.setAttribute('aria-expanded', 'false'); moreCountries.textContent = countryMoreLabel;
          applyFilters(chapter);
          (quick.querySelector('[aria-pressed="true"]') || moreCountries).focus({ preventScroll: true });
        };
        const countryButton = (value) => {
          const button = makeChoice(value, value || countrySelect.options[0]?.textContent || '', countrySelect, chooseCountry);
          const flag = countryFlag(codeForName.get(value));
          if (flag) button.prepend(flag);
          button.setAttribute('aria-pressed', String(state.selectedSmallCountry === value));
          return button;
        };
        const renderCountryList = () => countryList.replaceChildren(
          ...[...countryCounts.keys()].filter((name) => !state.countryFeaturedNames?.has(name))
            .sort((a, b) => a.localeCompare(b, language.toLowerCase())).map(countryButton));
        const renderCountryQuick = () => {
          const pinned = ['NL','BE'].map((code) => codes.get(code)).filter((name) => countryCounts.has(name));
          const ranked = [...countryCounts.keys()].filter((name) => !pinned.includes(name)).sort((a, b) =>
            countryCounts.get(b) - countryCounts.get(a) || a.localeCompare(b, language.toLowerCase()));
          const featured = [...pinned, ...ranked.slice(0, 6 - pinned.length)];
          const selected = state.selectedSmallCountry || '';
          if (selected && !featured.includes(selected)) {
            if (featured.length === 6) featured.pop();
            featured.splice(pinned.length, 0, selected);
          }
          const compact = Boolean(selected);
          const visible = compact ? [selected] : featured;
          state.countryFeaturedNames = new Set(visible);
          const allInline = countryCounts.size <= 6;
          if (compact || allInline) countryPanel.hidden = true;
          else if (state.countryAllChosen) countryPanel.hidden = false;
          moreCountries.textContent = countryMoreLabel;
          moreCountries.setAttribute('aria-expanded', String(!countryPanel.hidden));
          moreCountries.hidden = compact || allInline || !countryPanel.hidden;
          quick.replaceChildren(countryButton(''), ...visible.map(countryButton), countryPanel, moreCountries);
          renderCountryList();
        };
        if (countryCounts.size > 1) {
          if (countryWrap) countryWrap.hidden = true;
          picker.hidden = false;
          moreCountries.hidden = countryCounts.size <= 6;
          moreCountries.addEventListener('click', () => {
            countryPanel.hidden = !countryPanel.hidden;
            moreCountries.setAttribute('aria-expanded', String(!countryPanel.hidden));
            moreCountries.textContent = countryMoreLabel;
            moreCountries.hidden = !countryPanel.hidden || Boolean(state.selectedSmallCountry);
            if (!countryPanel.hidden) { renderCountryList(); countryList.querySelector('button')?.focus({ preventScroll: true }); }
          });
          countryPanel.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return;
            countryPanel.hidden = true; moreCountries.setAttribute('aria-expanded', 'false'); moreCountries.textContent = countryMoreLabel;
            moreCountries.hidden = Boolean(state.selectedSmallCountry) || countryCounts.size <= 6;
            if (!moreCountries.hidden) moreCountries.focus();
          });
          renderCountryQuick();
        } else if (countryWrap) countryWrap.hidden = true;
        const chooseCity = (value) => {
          state.showAllCities = value === '' && citySelect.options.length > 7 ? !state.showAllCities : false;
          state.secondaryAllChosen = value === '';
          citySelect.value = value;
          const resultsSearch = panel.querySelector('[data-f2w-sport-filter-search]');
          if (resultsSearch) resultsSearch.value = '';
          applyFilters(chapter);
          cityQuick.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
        };
        const cityButton = (value, label) => {
          const button = makeChoice(value, label, citySelect, chooseCity);
          if (value === '') {
            button.setAttribute('aria-pressed', String(!!state.secondaryAllChosen));
            button.setAttribute('aria-expanded', String(!!state.showAllCities));
          }
          return button;
        };
        state.renderSmallSecondary = (names, visible) => {
          state.hasSecondaryChoices = visible;
          if (!visible) { state.showAllCities = false; state.secondaryAllChosen = false; cityQuick.replaceChildren(); return; }
          const counts = new Map();
          const field = chapter === 'fighters' ? 'homeGym' : 'city';
          for (const item of state.items) {
            if (item.country !== state.selectedSmallCountry || !item[field]) continue;
            counts.set(item[field], (counts.get(item[field]) || 0) + 1);
          }
          const ranked = [...names].sort((a, b) => (counts.get(b) || 0) - (counts.get(a) || 0) || a.localeCompare(b, language.toLowerCase()));
          const featured = ranked.slice(0, 6);
          if (citySelect.value && !featured.includes(citySelect.value)) {
            if (featured.length === 6) featured.pop();
            featured.unshift(citySelect.value);
          }
          const extras = state.showAllCities ? names.filter((name) => !featured.includes(name)) : [];
          const allLabel = citySelect.options[0]?.textContent || '';
          cityQuick.replaceChildren(cityButton('', allLabel),
            ...[...featured, ...extras].map((name) => cityButton(name, name)));
        };
        state.renderSmallCountry = renderCountryQuick;
      };
      state.setupSmallPickers = setupSmallPickers;
      try {
        state.items = await hydrate(chapter, allIds);
        setupFilters(chapter);
        applyFilters(chapter);
      } catch (error) {
        states.delete(chapter);
        if (status) status.textContent = labels.error;
      }
    };

    const activate = (chapter, updateUrl) => {
      if (!tabs.has(chapter)) return;
      tabs.forEach((tab, key) => tab.setAttribute('aria-selected', key === chapter ? 'true' : 'false'));
      panels.forEach((panel, key) => { panel.hidden = key !== chapter; });
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('chapter', chapter);
        window.history.replaceState({}, '', url);
      }
      loadChapter(chapter);
    };

    const tabList = [...tabs.values()];
    tabList.forEach((tab, position) => {
      tab.addEventListener('click', () => activate(tab.dataset.f2wSportTab, true));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = tabList[(position + (event.key === 'ArrowRight' ? 1 : tabList.length - 1)) % tabList.length];
        next.focus();
        activate(next.dataset.f2wSportTab, true);
      });
    });

    root.querySelectorAll('[data-f2w-sport-jump]').forEach((link) => link.addEventListener('click', (event) => {
      const chapter = link.dataset.f2wSportJump;
      if (!tabs.has(chapter)) return;
      event.preventDefault();
      activate(chapter, true);
      root.querySelector('[data-f2w-sport-pills]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));

    const contentTabs = [...root.querySelectorAll('[data-f2w-sport-content-tab]')];
    const contentPanels = [...root.querySelectorAll('[data-f2w-sport-content]')];
    const openContent = (key) => {
      contentTabs.forEach((tab) => tab.setAttribute('aria-selected', tab.dataset.f2wSportContentTab === key ? 'true' : 'false'));
      contentPanels.forEach((panel) => { panel.hidden = panel.dataset.f2wSportContent !== key; });
    };
    contentTabs.forEach((tab, position) => {
      tab.addEventListener('click', () => openContent(tab.dataset.f2wSportContentTab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = contentTabs[(position + (event.key === 'ArrowRight' ? 1 : contentTabs.length - 1)) % contentTabs.length];
        next.focus();
        openContent(next.dataset.f2wSportContentTab);
      });
    });
    if (contentTabs.length) openContent(contentTabs[0].dataset.f2wSportContentTab);
    else root.querySelector('[data-f2w-sport-content-pills]')?.setAttribute('hidden', '');

    const requested = new URL(window.location.href).searchParams.get('chapter');
    const nav = root.querySelector('[data-f2w-sport-pills]');
    if (!tabList.length) {
      if (nav) nav.hidden = true;
      return;
    }
    activate(tabs.has(requested) ? requested : tabList[0].dataset.f2wSportTab, false);
  };

  roots.forEach(init);
})();


























